/**
 * AB#21732 — what an action button does, in one place.
 *
 * ELSA's "Do this next" used to offer Add internal comment and Open order
 * communication whatever the recommendation said, and the Needs Attention
 * buttons carried a code nothing read. The backend now attaches a code from a
 * closed set to each action (ElsaActions.php) and the queue uses the same
 * codes; this maps each code to a label, who may use it, and — in OrderDetail —
 * the real control it opens.
 *
 * A code the viewer cannot use falls back to an internal note (the one control
 * nearly everyone has), so a button never leads to a 403 or a dead end.
 */

export type ElsaActionCode =
  | 'set_in_hand_date'
  | 'set_ship_date'
  | 'get_tracking'
  | 'reply_factory'
  | 'update_customer'
  | 'fix_order_type'
  | 'assign_factory'
  | 'add_internal_note'
  // AB#22511 — a consolidation_wait card: the order in the Shipping queue,
  // where its group is managed.
  | 'open_shipping';

/** What the order screen can carry out; anything else opens the order only. */
export type OrderActionCode =
  | ElsaActionCode
  | 'open_elsa'
  | 'view_order'
  // AB#21997 — finance cards: show the order with every invoice listed.
  | 'open_invoices';

export interface ElsaActionRequest {
  code: OrderActionCode;
  /** Y-m-d, for set_in_hand_date / set_ship_date. */
  date?: string | null;
  /** Draft text for an internal note (Talk to ELSA). */
  text?: string | null;
}

export interface ActionAbilities {
  can: (permission: string) => boolean;
  /** HummelUS orders have Zendesk threads; others have no communication panel. */
  hasSupportThreads: boolean;
}

interface Entry {
  label: string;
  available: (a: ActionAbilities) => boolean;
}

const REGISTRY: Record<ElsaActionCode, Entry> = {
  set_in_hand_date: { label: 'Set in-hand date', available: (a) => a.can('enter-in-hand-date') },
  set_ship_date: { label: 'Set ship date', available: (a) => a.can('enter-shipping-date') },
  get_tracking: { label: 'Enter shipping details', available: (a) => a.can('enter-shipping-date') },
  reply_factory: { label: 'Reply to factory', available: (a) => a.hasSupportThreads },
  update_customer: { label: 'Update customer', available: (a) => a.hasSupportThreads },
  fix_order_type: { label: 'Change order type', available: (a) => a.can('change-order-type') },
  // Assigning a factory lives on the orders list, not the order pane.
  assign_factory: { label: 'Assign factory', available: () => false },
  add_internal_note: {
    label: 'Add internal note',
    available: (a) => a.can('add-internal-order-comment'),
  },
  open_shipping: {
    label: 'Open Shipping',
    available: (a) =>
      a.can('view-shipping') || a.can('operate-shipping') || a.can('manage-shipping'),
  },
};

export const isElsaActionCode = (value: unknown): value is ElsaActionCode =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(REGISTRY, value);

export const isOrderActionCode = (value: unknown): value is OrderActionCode =>
  isElsaActionCode(value) ||
  value === 'open_elsa' ||
  value === 'view_order' ||
  value === 'open_invoices';

/** "23 Sep 2026" from "2026-09-23"; null for anything else. */
export const shortDate = (ymd?: string | null): string | null => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  // Spelled out rather than toLocaleDateString: locales disagree ("Sep" vs "Sept").
  const [y, m, d] = ymd.split('-').map(Number);
  const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return m >= 1 && m <= 12 && d >= 1 && d <= 31 ? `${d} ${MONTHS[m - 1]} ${y}` : null;
};

export interface ResolvedButton {
  code: ElsaActionCode;
  label: string;
  date: string | null;
}

/**
 * The buttons for one action: its own control first when the viewer can use
 * it, then an internal note as the alternative. Never two notes, never a
 * control the viewer cannot use.
 */
export const buttonsFor = (
  action: { do?: string | null; date?: string | null } | null | undefined,
  abilities: ActionAbilities
): ResolvedButton[] => {
  const out: ResolvedButton[] = [];
  const code = action?.do;

  if (isElsaActionCode(code) && REGISTRY[code].available(abilities)) {
    const date = action?.date ?? null;
    const when = code === 'set_in_hand_date' || code === 'set_ship_date' ? shortDate(date) : null;
    out.push({
      code,
      label: when ? `${REGISTRY[code].label} · ${when}` : REGISTRY[code].label,
      date,
    });
  }

  if (out[0]?.code !== 'add_internal_note' && REGISTRY.add_internal_note.available(abilities)) {
    out.push({ code: 'add_internal_note', label: REGISTRY.add_internal_note.label, date: null });
  }

  return out;
};

/** The plain label for a code (queue buttons come with their own label). */
export const labelFor = (code: string): string | null =>
  isElsaActionCode(code) ? REGISTRY[code].label : null;
